import { api, formatApiError } from "@/lib/api";
import { BRAND } from "@/config/brand";

/**
 * Unified Razorpay checkout for the entire app.
 * paymentType: "appointment" | "membership"
 * referenceId: booking_id (for appointment) OR undefined (for membership - plan is used instead)
 * plan:        "monthly" | "yearly"  (only for membership)
 * description: text shown in the checkout modal
 */
export async function openRazorpayCheckout({
  paymentType,
  referenceId,
  plan,
  description,
  prefill = {},
  onSuccess,
  onFailure,
  onDismiss,
}) {
  if (typeof window === "undefined" || !window.Razorpay) {
    throw new Error("Payment library not loaded. Please refresh and try again.");
  }

  const createUrl =
    paymentType === "membership"
      ? "/razorpay/create-membership-order"
      : "/razorpay/create-order";
  const verifyUrl =
    paymentType === "membership"
      ? "/razorpay/verify-membership-payment"
      : "/razorpay/verify-payment";

  const orderPayload =
    paymentType === "membership" ? { plan } : { booking_id: referenceId };

  let order;
  try {
    const { data } = await api.post(createUrl, orderPayload);
    order = data;
  } catch (err) {
    throw new Error(formatApiError(err));
  }

  return new Promise((resolve) => {
    const rzp = new window.Razorpay({
      key: order.key_id,
      amount: order.amount,
      currency: order.currency,
      name: BRAND.name,
      description: description || "Secure payment",
      order_id: order.order_id,
      prefill: {
        name: prefill.name || order.name || "",
        email: prefill.email || order.email || "",
      },
      theme: { color: BRAND.themeColor },
      modal: {
        ondismiss: () => {
          onDismiss?.();
          resolve({ status: "dismissed" });
        },
      },
      handler: async (resp) => {
        const verifyPayload =
          paymentType === "membership"
            ? {
                plan,
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_signature: resp.razorpay_signature,
              }
            : {
                booking_id: referenceId,
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_signature: resp.razorpay_signature,
              };
        try {
          const { data } = await api.post(verifyUrl, verifyPayload);
          onSuccess?.(data);
          resolve({ status: "success", data });
        } catch (err) {
          const msg = formatApiError(err);
          onFailure?.(msg);
          resolve({ status: "failed", error: msg });
        }
      },
    });
    rzp.on("payment.failed", (resp) => {
      const msg = resp?.error?.description || "Payment failed";
      onFailure?.(msg);
      resolve({ status: "failed", error: msg });
    });
    rzp.open();
  });
}
